import { OpportunityCard, OpportunityCardContent } from './OpportunityCard';
import { Handshake } from 'lucide-react';
import { createOpportunityClientsTableColumns } from './OpportunityClientsTable';
import { DataTable } from './DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState, useMemo } from 'react';
import {
  EngagementSheetPrimaryBtn,
  OpportunityEngagementSheet,
} from '@/components/OpportunityEngagementSheet';
import { reviewOnboardingSlidesReminderNoLinkTemplate } from '@shared';
import {
  useHandleEmailSentStatus,
  useOpportunityState,
} from '@/pages/AdvisorDashboardOpportunities';
import { SendOpportunityMessageModal } from './SendOpportunityMessageModal';

export function OpportunityReviewResultsWithClient() {
  const {
    advisorOpportunitiesState,
    status,
    priorityStatus,
    clientsEligibleForReminder,
    qualifyingClients,
  } = useOpportunityState('REVIEW_RESULTS_TOGETHER');
  const [sheetOpen, setSheetOpen] = useState(false);

  useHandleEmailSentStatus(
    advisorOpportunitiesState,
    () => setSheetOpen(false),
    'REVIEW_RESULTS_TOGETHER',
  );

  const engagementSheet = useMemo(
    () => (
      <OpportunityReviewResultsEngagementSheet
        sheetOpen={sheetOpen}
        setSheetOpen={setSheetOpen}
        qualifyingClients={qualifyingClients}
        clientsEligibleForReminder={clientsEligibleForReminder}
        buttonTrigger={
          <EngagementSheetPrimaryBtn
            priorityStatus={priorityStatus}
            onClick={() => setSheetOpen(true)}
            actionText={`View Clients`}
          />
        }
      />
    ),
    [sheetOpen, qualifyingClients, clientsEligibleForReminder, priorityStatus],
  );

  if (priorityStatus === 'Not applicable') return null;

  return (
    <OpportunityCard
      priorityStatus={priorityStatus}
      isLoading={status === 'fetchingClients'}
      cardContent={
        <OpportunityCardContent
          icon={
            <Handshake
              className="h-8 w-8"
              strokeWidth={1}
            />
          }
          primaryMetric={
            qualifyingClients ? qualifyingClients.length.toString() : '0'
          }
          metricDescription={`${qualifyingClients.length === 1 ? 'Client' : 'Clients'} who ${
            qualifyingClients.length === 1 ? 'needs' : 'need'
          } to review their results with you.`}
          secondaryMetric={
            clientsEligibleForReminder
              ? clientsEligibleForReminder.length.toString()
              : '0'
          }
          secondaryMetricDescription={`${
            clientsEligibleForReminder
              ? clientsEligibleForReminder.length === 1
                ? 'client needs a reminder'
                : 'clients need reminders'
              : '0'
          }`}
        />
      }
      cardFooterContent={engagementSheet}
      data-testid="opportunity-card-review-results-together"
    />
  );
}

function useEmailTemplateState(advisor: Advisor) {
  const [advisorProvidedBody, setAdvisorProvidedBody] = useState<
    string | undefined
  >(undefined);
  const [isEditMessageOpen, setIsEditMessageOpen] = useState(false);

  const handleUpdateMessage = (body: string) => {
    setAdvisorProvidedBody(body);
    setIsEditMessageOpen(false);
  };

  return {
    advisorProvidedBody,
    isEditMessageOpen,
    setIsEditMessageOpen,
    handleUpdateMessage,
  };
}

function OpportunityReviewResultsEngagementSheet({
  sheetOpen,
  setSheetOpen,
  qualifyingClients,
  clientsEligibleForReminder,
  buttonTrigger,
}: {
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
  qualifyingClients: Client[];
  clientsEligibleForReminder: Client[];
  buttonTrigger: React.ReactNode;
}) {
  const { advisor, clientsIsLoading, status } = useOpportunityState(
    'REVIEW_RESULTS_TOGETHER',
  );
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const { advisorProvidedBody, isEditMessageOpen, setIsEditMessageOpen } =
    useEmailTemplateState(advisor);

  useEffect(() => {
    if (!sheetOpen) {
      setSelectedClients([]);
    }
  }, [sheetOpen]);

  const isLoading = status === 'sendingEmails';

  const MemoizedDataTable = useMemo(
    () => (
      <DataTable<Client>
        key={qualifyingClients.map(c => c.clientId).join('-')}
        columns={
          createOpportunityClientsTableColumns(
            'REVIEW_RESULTS_TOGETHER',
          ) as ColumnDef<Client>[]
        }
        getRowId={row => row.clientId}
        data={qualifyingClients}
        isLoading={clientsIsLoading}
        className="mb-12"
        tableId="reviewResultsTable"
        variant="outlined"
        tableSkeletonRows={10}
        defaultSelection={clientsEligibleForReminder.filter(
          client => client.clientEmail,
        )}
        pageSize={8}
        hideColumnSelector={true}
        onSelectionChange={setSelectedClients}
        isRowDeactivated={row => !row.clientEmail}
      />
    ),
    [qualifyingClients, clientsIsLoading, clientsEligibleForReminder],
  );

  return (
    <>
      <OpportunityEngagementSheet
        trigger={buttonTrigger}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title="Clients awaiting result review"
        description="Clients who have completed intake forms but have not reviewed results with you based on their status. Auto-selected for next steps."
        clientsTable={MemoizedDataTable}
        floatingMenuCtaProps={{
          text: `Review and send requests`,
          onClick: () => setIsEditMessageOpen(true),
          disabled: isLoading,
        }}
        selectedClientCount={selectedClients.length}
      />
      <SendOpportunityMessageModal
        isOpen={isEditMessageOpen}
        onClose={() => setIsEditMessageOpen(false)}
        advisor={advisor}
        defaultMessage={advisorProvidedBody}
        emailTemplate={reviewOnboardingSlidesReminderNoLinkTemplate}
        emailTemplateProps={{
          advisorFirstName: advisor.advisorFirstName,
          clientName: '{{Client Name}}',
          advisorLastName: advisor.advisorLastName,
          advisorEmail: advisor.advisorEmail,
          schedulingMeetingLink: advisor.schedulingLinkUrl,
          emailPreferencesLink: '',
          isPreview: true,
        }}
        selectedClients={selectedClients}
        engagementType="REVIEW_RESULTS_TOGETHER"
      />
    </>
  );
}
